'use client'

import { getOrderStatus } from '@/features/payment'
import { getOrdersHistory } from '@/services'
import { useAccountStore } from '@/store'
import { TypePopup, useUpsellStore } from '@/store/upsell'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

export const useSetTheLatestOrder = () => {
  const { orderCode } = useUpsellStore()
  const { isAuthenticated } = useAccountStore()

  // Track xem đã set order code từ history chưa - CHỈ reset khi logout
  const hasSetOrderFromHistory = useRef(false)
  // Track order ID đã được set để tránh set lại cùng một order
  const processedOrderId = useRef<string | null>(null)
  // Track authentication state để phát hiện logout
  const wasAuthenticated = useRef(isAuthenticated)

  // Fetch danh sách đơn hàng chỉ khi chưa có orderCode và user đã login
  const { data: ordersHistory } = useQuery({
    queryKey: ['ORDERS_HISTORY'],
    queryFn: () =>  
      getOrdersHistory({
        currentPage: 1,
        pageSize: 1,
        sort: {
          sort_field: 'CREATED_AT',
          sort_direction: 'DESC',
        },
        scope: 'GLOBAL',
        status: 'PENDING',
      }),
    enabled: isAuthenticated && !orderCode && !hasSetOrderFromHistory.current,
    staleTime: 5 * 60 * 1000, // 5 phút
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false, 
    retry: 1,
  })

  const handleCheckOrderAlreadyProcessed = async (orderId: string) => {
    try {
      const res = await getOrderStatus(orderId)
      const isCheckoutCompleted = res?.state === 'processing'
      return isCheckoutCompleted
    } catch (error) {
      return true // Giả định đã process nếu có lỗi
    }
  }



  // Set order code từ history chỉ một lần duy nhất cho mỗi session
  useEffect(() => {
    if (hasSetOrderFromHistory.current) {
      return
    }

    // Nếu đã có orderCode (từ localStorage hoặc đã set) thì không làm gì
    if (orderCode) {
      return
    }

    const items = ordersHistory?.data?.items
    if (!items || items.length === 0) {
      return
    }

    const latestOrder = items[0]
    if (!latestOrder?.id) {
      return
    }

    try {
      const decodeOrderId = atob(latestOrder.id)
      
      // Kiểm tra xem order này đã được process chưa
      if (processedOrderId.current === decodeOrderId) {
        return
      }

      // Get fresh state từ store để tránh stale closure
      const currentState = useUpsellStore.getState()

      const orderNumber = latestOrder?.number
      // nếu đơn không có mã thì không làm gì nữa
      if (!orderNumber) {
        return
      }

      handleCheckOrderAlreadyProcessed(orderNumber).then((isProcessed) => {
        // Triple check trước khi set
        if (!currentState.orderCode && !hasSetOrderFromHistory.current) {
          // nếu đơn chưa được thanh toán thì set popup thanh toán ngược lại set vào popup upsell
          if (!isProcessed) {
            currentState.setOrderCode(orderNumber, TypePopup.PAYMENT)
            return
          } else {
            currentState.setOrderCode(decodeOrderId, TypePopup.UPSELL)
          }

          // Mark đã set và CHỈ reset khi logout
          hasSetOrderFromHistory.current = true
          processedOrderId.current = decodeOrderId
        }
      })
      
    } catch (error) {
      console.error('❌ [useSetTheLatestOrder] Error decoding order ID:', error)
    }
    // CHỈ depend vào ordersHistory data, KHÔNG depend vào orderCode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersHistory?.data?.items])

  // CHỈ reset flags khi user LOGOUT (isAuthenticated: true → false)
  // KHÔNG reset khi orderCode bị clear do expired/empty offers
  useEffect(() => {
    // Phát hiện logout: wasAuthenticated = true → isAuthenticated = false
    if (wasAuthenticated.current && !isAuthenticated) {
      hasSetOrderFromHistory.current = false
      processedOrderId.current = null
    }
    
    // Update tracking state
    wasAuthenticated.current = isAuthenticated
  }, [isAuthenticated])
}
